import type { OperationCommand, OperationContext } from '@/domain'

import type { Json } from './database.types'
import type { CentimeClient } from './supabase'

/**
 * Envoi d'une opération à la base. La base rejoue toutes les règles du domaine dans une
 * transaction et renvoie soit un succès (éventuellement « déjà enregistré »), soit un refus métier.
 * Une erreur réseau ou technique est levée : l'opération peut être renvoyée sans risque de
 * doublon, son identifiant restant le même.
 */

export type RemoteOperationResult =
  | { ok: true; operationId: string; duplicate: boolean; requiresVerification: boolean }
  | { ok: false; error: { code: string } & Record<string, unknown> }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function parseRemoteResult(value: Json): RemoteOperationResult {
  if (isRecord(value) && value.ok === true && typeof value.operationId === 'string') {
    return {
      ok: true,
      operationId: value.operationId,
      duplicate: value.duplicate === true,
      requiresVerification: value.requiresVerification === true,
    }
  }
  if (isRecord(value) && value.ok === false && isRecord(value.error)) {
    const { error } = value
    if (typeof error.code === 'string') return { ok: false, error: { ...error, code: error.code } }
  }
  throw new Error(`Réponse inattendue du serveur : ${JSON.stringify(value)}`)
}

async function call(context: OperationContext, client: CentimeClient, command: OperationCommand) {
  const base = { p_operation_id: context.operationId, p_occurred_at: context.occurredAt }
  switch (command.kind) {
    case 'income':
      return client.rpc('record_income', {
        ...base,
        p_amount: command.amount,
        p_title: command.title,
        ...(command.occurrenceId === undefined ? {} : { p_occurrence_id: command.occurrenceId }),
      })
    case 'allocate':
      return client.rpc('allocate_funds', { ...base, p_lines: command.lines })
    case 'expense':
      return client.rpc('record_expense', {
        ...base,
        p_envelope_id: command.envelopeId,
        p_amount: command.amount,
        p_title: command.title,
      })
    case 'transfer':
      return client.rpc('transfer_funds', {
        ...base,
        p_source_envelope_id: command.sourceEnvelopeId,
        p_destination_envelope_id: command.destinationEnvelopeId,
        p_amount: command.amount,
        p_origin: command.origin,
        ...(command.note === undefined ? {} : { p_note: command.note }),
      })
    case 'reserve':
      return client.rpc('reserve_occurrence', {
        ...base,
        p_envelope_id: command.envelopeId,
        p_occurrence_id: command.occurrenceId,
        p_amount: command.amount,
      })
    case 'pay':
      return client.rpc('pay_occurrence', {
        ...base,
        p_envelope_id: command.envelopeId,
        p_occurrence_id: command.occurrenceId,
        p_amount: command.amount,
        p_title: command.title,
      })
    case 'release':
      return client.rpc('release_occurrence', { ...base, p_occurrence_id: command.occurrenceId })
    case 'adjust':
      return client.rpc('adjust_balance', {
        ...base,
        // `null` vise l'argent non affecté : le paramètre SQL l'accepte, le type généré l'ignore.
        p_envelope_id: command.envelopeId as unknown as string,
        p_direction: command.direction,
        p_amount: command.amount,
        p_reason: command.reason,
      })
    case 'reverse':
      return client.rpc('reverse_operation', {
        ...base,
        p_target_operation_id: command.operationId,
        p_reason: command.reason,
      })
    case 'unallocate':
      return client.rpc('return_to_unallocated', {
        ...base,
        p_envelope_id: command.envelopeId,
        p_amount: command.amount,
      })
  }
}

export async function submitOperation(
  client: CentimeClient,
  context: OperationContext,
  command: OperationCommand,
): Promise<RemoteOperationResult> {
  const { data, error } = await call(context, client, command)
  if (error) throw error
  return parseRemoteResult(data)
}

/** Opérations modifiables : leur nouvelle version remplace l'originale (spec §5.7). */
export type ReplacementCommand = Extract<
  OperationCommand,
  { kind: 'expense' | 'income' | 'transfer' | 'pay' | 'adjust' }
>

/**
 * Modifie une opération : annulation de l'originale et enregistrement de la nouvelle version
 * dans une seule transaction côté base. Refusée sans rien changer si la nouvelle version l'est.
 */
export async function replaceOperation(
  client: CentimeClient,
  context: OperationContext & { reversalId: string; targetOperationId: string },
  command: ReplacementCommand,
): Promise<RemoteOperationResult> {
  const { data, error } = await client.rpc('replace_operation', {
    p_reversal_id: context.reversalId,
    p_operation_id: context.operationId,
    p_occurred_at: context.occurredAt,
    p_target_operation_id: context.targetOperationId,
    p_command: { ...command } as Json,
  })
  if (error) throw error
  return parseRemoteResult(data)
}
